// KI-Einstellungen und SEPA (design_handoff_control_center §4, Screens 01/04). Reine Darstellung:
// Modell-Select → Segment, Zahlenfeld → Stepper −/+ (gleiche Werte 50–100 %, Schritt 5); gespeichert wird über die
// unveränderten Handler kiSpeichern / schwelleSpeichern / glaeubigerIdSpeichern.
import { useEffect, useState } from 'react';
import { SchalterZeile } from '../../../components/immobilien/ui';
import { ladeSchalter35a, setzeSchalter35a } from '../../../lib/buchhaltung/paragraph35a';
import { ladeSchalterErledigtBeiRechnung, setzeSchalterErledigtBeiRechnung } from '../../../lib/emails/erledigtInfo';
import { ladeSchalterTicketTitelObjekt, setzeSchalterTicketTitelObjekt } from '../../../lib/tickets/titelSchalter';
import { Bank, CheckCircle, IdentificationCard, Info, Minus, Plus, Sparkle, Wind } from '@phosphor-icons/react';
import { Segment } from '../../../components/ui/Segment';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { SCHWELLE_MAX, SCHWELLE_MIN, schwelleProzent, schwelleSchritt } from '../../../lib/controlCenter/logik';
import { CcWidget, KopfIcon } from './Rahmen';

export type KiModell = 'mistral' | 'gemini';

const verlauf = (token: string) => `linear-gradient(160deg, color-mix(in srgb, var(${token}) 65%, #fff), var(${token}))`;

function GespeichertChip() {
  return (
    <span role="status" className="inline-flex items-center" style={{ height: 24, padding: '0 10px', gap: 5, borderRadius: 12, fontSize: 12, fontWeight: 800, background: 'var(--ok-soft)', color: 'var(--ok)' }}>
      <CheckCircle size={14} weight="fill" />gespeichert
    </span>
  );
}

function Hinweis({ children }: { children: string }) {
  return (
    <p className="m-0 flex" style={{ gap: 9, padding: '12px 14px', borderRadius: 16, background: 'var(--fill)', fontSize: 12.5, lineHeight: 1.5, color: 'var(--ink2)' }}>
      <Info size={16} weight="fill" color="var(--ink3)" className="shrink-0" style={{ marginTop: 1 }} />
      <span>{children}</span>
    </p>
  );
}

const feldTitel = { fontSize: 12.5, fontWeight: 700, color: 'var(--ink2)' } as const;

export function KiWidget({ modell, schwelle, gespeichert, spalte, onModell, onSchwelle }: {
  modell: KiModell; schwelle: number; gespeichert: boolean; spalte: string;
  onModell: (m: KiModell) => void; onSchwelle: (prozent: number) => void;
}) {
  const prozent = schwelleProzent(schwelle);
  const naechst = (r: 1 | -1) => schwelleSchritt(prozent, r);
  const k = useMobilModus().mobil ? 44 : 36;
  const rund = { width: k, height: k, border: 'none', borderRadius: '50%', background: 'var(--fill2)', color: 'var(--ink)' } as const;
  return (
    <CcWidget titel="KI-Einstellungen" untertitel="Analyse eingehender E-Mails" spalte={spalte}
      icon={<KopfIcon flaeche={verlauf('--pur')} farbe="#fff"><Sparkle size={19} weight="fill" /></KopfIcon>}
      rechts={gespeichert ? <GespeichertChip /> : undefined}>
      <div className="flex flex-col" style={{ gap: 8 }}>
        <span style={feldTitel}>KI-Modell</span>
        <Segment<KiModell> ariaLabel="KI-Modell" wert={modell} onChange={(m) => { if (m !== modell) onModell(m); }} hoehe={44} voll
          eintraege={[
            { key: 'mistral', label: 'Mistral', icon: Wind, tip: 'Mistral – Für die Analyse eingehender E-Mails verwenden' },
            { key: 'gemini', label: 'Gemini', icon: Sparkle, tip: 'Gemini – Für die Analyse eingehender E-Mails verwenden' },
          ]} />
      </div>
      <div className="flex flex-col" style={{ gap: 8 }}>
        <span id="cc-schwelle" style={feldTitel}>Auto-Zuordnungs-Schwelle</span>
        <div role="group" aria-labelledby="cc-schwelle" className="flex items-center"
          style={{ gap: 12, padding: '10px 12px', borderRadius: 18, background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
          <button type="button" aria-label="Schwelle senken" data-tip="Schwelle senken – In 5-%-Schritten, minimal 50 %" disabled={prozent <= SCHWELLE_MIN}
            onClick={() => onSchwelle(naechst(-1))} className="flex shrink-0 items-center justify-center"
            style={{ ...rund, cursor: prozent <= SCHWELLE_MIN ? 'not-allowed' : 'pointer', opacity: prozent <= SCHWELLE_MIN ? 0.4 : 1 }}>
            <Minus size={15} weight="bold" />
          </button>
          <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 7 }}>
            <span aria-live="polite" className="text-center tabular-nums" style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1 }}>{prozent} %</span>
            <span aria-hidden className="flex overflow-hidden" style={{ height: 6, borderRadius: 3, background: 'var(--fill3)' }}>
              <span style={{ width: `${Math.min(100, Math.max(0, prozent))}%`, background: 'var(--acc-fill)', borderRadius: 3, transition: 'width .25s' }} />
            </span>
          </div>
          <button type="button" aria-label="Schwelle erhöhen" data-tip="Schwelle erhöhen – In 5-%-Schritten, maximal 100 %" disabled={prozent >= SCHWELLE_MAX}
            onClick={() => onSchwelle(naechst(1))} className="flex shrink-0 items-center justify-center"
            style={{ ...rund, cursor: prozent >= SCHWELLE_MAX ? 'not-allowed' : 'pointer', opacity: prozent >= SCHWELLE_MAX ? 0.4 : 1 }}>
            <Plus size={15} weight="bold" />
          </button>
        </div>
      </div>
      <Ki35aSchalter />
      <ErledigtBeiRechnungSchalter />
      <TicketTitelObjektSchalter />
      <Hinweis>Hinweis: OCR von Anhängen läuft immer über Mistral. Ab der Auto-Zuordnungs-Schwelle übernimmt die KI Treffer automatisch. API-Keys werden als Edge-Function-Secrets gepflegt.</Hinweis>
    </CcWidget>
  );
}

/** § 35a-Auslesung (#564 S5): KI liest Lohnanteile aus Rechnungs-PDFs – nur auf Knopfdruck, nur Vorschlag. Standard: aus. */
function Ki35aSchalter() {
  const [an, setAn] = useState<boolean | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  useEffect(() => { void ladeSchalter35a().then(setAn, () => setAn(false)); }, []);
  const umschalten = (neu: boolean) => {
    setFehler(null); setAn(neu);
    void setzeSchalter35a(neu).catch((e: Error) => { setAn(!neu); setFehler(e.message); });
  };
  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      <SchalterZeile an={an === true} onChange={umschalten} label="§ 35a-Lohnanteile aus Rechnungen lesen" disabled={an === null} />
      <Hinweis>Liest auf Knopfdruck Lohn-, Fahrt- und Materialanteil aus dem Rechnungs-PDF (Objekt › Finanzen bei WEG und in der Rechnung). Das Ergebnis ist nur ein Vorschlag mit Fundstelle; erst „Übernehmen“ trägt den Betrag ein. Ausgeschaltet bleibt die Eingabe von Hand möglich.</Hinweis>
      {fehler && <span style={{ fontSize: 12, color: 'var(--bad)' }}>Nicht gespeichert: {fehler}</span>}
    </div>
  );
}

/** #573: Rechnung aus dem Mail-Anhang erfasst → Mail automatisch erledigt (seit 22.07.2026). Standard: an. */
function ErledigtBeiRechnungSchalter() {
  const [an, setAn] = useState<boolean | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  useEffect(() => { void ladeSchalterErledigtBeiRechnung().then(setAn, () => setAn(true)); }, []);
  const umschalten = (neu: boolean) => {
    setFehler(null); setAn(neu);
    void setzeSchalterErledigtBeiRechnung(neu).catch((e: Error) => { setAn(!neu); setFehler(e.message); });
  };
  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      <SchalterZeile an={an === true} onChange={umschalten} label="E-Mail erledigen, wenn die Rechnung erfasst ist" disabled={an === null} />
      <Hinweis>Erkennt die KI im Anhang einer E-Mail eine Rechnung und legt sie unter Rechnungen an, wandert die E-Mail automatisch nach „Erledigt“. Ausgenommen sind E-Mails, die einer Person zugewiesen sind oder gerade bearbeitet werden. Ausgeschaltet bleiben alle E-Mails offen, bis jemand sie von Hand erledigt; die Rechnung wird trotzdem erfasst.</Hinweis>
      {fehler && <span style={{ fontSize: 12, color: 'var(--bad)' }}>Nicht gespeichert: {fehler}</span>}
    </div>
  );
}

/** #580: Objektnummer + Straße vorn im Ticket-Titel, bei jedem Anlageweg (DB-Trigger). Standard: an. */
function TicketTitelObjektSchalter() {
  const [an, setAn] = useState<boolean | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  useEffect(() => { void ladeSchalterTicketTitelObjekt().then(setAn, () => setAn(true)); }, []);
  const umschalten = (neu: boolean) => {
    setFehler(null); setAn(neu);
    void setzeSchalterTicketTitelObjekt(neu).catch((e: Error) => { setAn(!neu); setFehler(e.message); });
  };
  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      <SchalterZeile an={an === true} onChange={umschalten} label="Objekt im Ticket-Titel" disabled={an === null} />
      <Hinweis>Hat ein Ticket ein Objekt, steht vorn im Titel automatisch Objektnummer und Straße, z. B. „0018 Schulstraße 18 · Heizung defekt“. Das gilt für Tickets aus E-Mails, aus Mieter- und Eigentümerportal und von Hand, auch wenn das Objekt erst später gesetzt oder gewechselt wird. Steht die Adresse schon im Titel, bleibt er unverändert. Ausgeschaltet bleibt der Titel so, wie er eingegeben oder von der KI vorgeschlagen wurde.</Hinweis>
      {fehler && <span style={{ fontSize: 12, color: 'var(--bad)' }}>Nicht gespeichert: {fehler}</span>}
    </div>
  );
}

export function SepaWidget({ wert, gespeichert, spalte, onAendern, onVerlassen }: {
  wert: string; gespeichert: boolean; spalte: string; onAendern: (v: string) => void; onVerlassen: () => void;
}) {
  const [fokus, setFokus] = useState(false);
  return (
    <CcWidget titel="SEPA" untertitel="Zentrale Gläubiger-ID" spalte={spalte}
      icon={<KopfIcon flaeche={verlauf('--ok')} farbe="#fff"><Bank size={19} weight="fill" /></KopfIcon>}
      rechts={gespeichert ? <GespeichertChip /> : undefined}>
      <label className="flex flex-col" style={{ gap: 8 }}>
        <span style={feldTitel}>Gläubiger-Identifikationsnummer</span>
        <span data-tip="Gläubiger-ID – Wird beim Verlassen des Feldes automatisch gespeichert" className="flex items-center"
          style={{ gap: 10, height: 50, padding: '0 14px', borderRadius: 16, background: 'var(--fill)', transition: 'box-shadow .2s',
            boxShadow: fokus ? 'inset 0 0 0 2px var(--acc)' : 'inset 0 0 0 1px var(--line)' }}>
          <IdentificationCard size={18} weight="bold" color="var(--ink3)" />
          <input type="text" value={wert} onChange={(e) => onAendern(e.target.value)} placeholder="DE00ZZZ00000000000"
            onFocus={() => setFokus(true)} onBlur={() => { setFokus(false); onVerlassen(); }}
            className="min-w-0 flex-1"
            style={{ border: 'none', outline: 'none', background: 'transparent', fontFamily: "'SF Mono', ui-monospace, Menlo, monospace", fontSize: 15, fontWeight: 600, letterSpacing: '0.04em', color: 'var(--ink)' }} />
        </span>
      </label>
      <Hinweis>Wird beim Vorausfüllen von SEPA-Mandaten verwendet, wenn beim Eigentümer keine eigene Gläubiger-ID hinterlegt ist.</Hinweis>
    </CcWidget>
  );
}
